import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Navigation2,
  Download,
  RefreshCw,
  Clock,
  Package,
  Truck,
  User,
  Phone,
  Mail,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Map,
  Activity,
  FileText,
  Star,
  Navigation,
  MessageSquare,
  Camera,
  Eye,
  Upload,
  Container,
  Settings,
  Bell,
  Save,
  Users,
  Building2,
  ChevronRight,
  ExternalLink,
  RotateCcw,
  AlertCircle,
  Info,
  CheckSquare,
  TrendingUp,
  Hash,
  Timer,
  Plus,
  ChevronDown,
  ChevronUp,
  Ruler,
} from "lucide-react";

import { Button, Badge, Card } from "../components/ui";
import { API_BASE_URL, API_ENDPOINTS } from "../constants/index";
import MapView from "../components/ui/MapView";
import ConsentStatus from "../components/ui/ConsentStatus";

import GooglePlacesAutocomplete from "../components/google/GooglePlacesAutocomplete";
import TollWeatherTracker from "../components/google/TollWeatherTracker";
import { useMasterData } from "../hooks/master-data-hooks";
import { inputClassName, labelClassName } from "../styles/utility-classes";

const JobDetailsPage = () => {
  const [loading, setLoading] = useState(true);
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedLeg, setSelectedLeg] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [legStatuses, setLegStatuses] = useState([]);
  const [jobStatuses, setJobStatuses] = useState([]);
  const [updatingLegStatus, setUpdatingLegStatus] = useState(false);
  const [updatingJobStatus, setUpdatingJobStatus] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Modal states
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [selectedLegForUpdate, setSelectedLegForUpdate] = useState(null);

  const [showAddLegModal, setShowAddLegModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalType, setStatusModalType] = useState(""); // 'job' or 'leg'
  const [selectedStatusItem, setSelectedStatusItem] = useState(null);
  const [notification, setNotification] = useState(null);
  const [vehiclesByTransporter, setVehiclesByTransporter] = useState({});
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);

  const [showTollTrackerModal, setShowTollTrackerModal] = useState(false);
  const [selectedLegForTollTracker, setSelectedLegForTollTracker] =
    useState(null);

  useEffect(() => {
    get_job_details();
  }, [jobId]);

  useEffect(() => {
    fetchLegStatuses();
    fetchJobStatuses();
  }, []);

  const {
    customers,
    vendors,
    ports,
    warehouses,
    transporters,
    containers,
    goodsTypes,
    isLoading: masterDataLoading,
    error: masterDataError,
  } = useMasterData();

  useEffect(() => {
    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyARm0zb4v57rZQ0XI9QfAb93MDm-wrgZw8&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setGoogleMapsLoaded(true);
      script.onerror = () => console.error("Failed to load Google Maps API");
      document.head.appendChild(script);
    };
    loadGoogleMaps();
  }, []);

  const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in kilometers
    return Math.round(distance);
  }, []);

  const getVehiclesForTransporter = useCallback(async (transporterCode) => {
    if (!transporterCode) return [];

    try {
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.TRANSPORTERWISEVEHICLELIST}?transporter_code=${transporterCode}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 200) {
        return result.data.map((item) => ({
          value: item.vehicle_id.toString(),
          label: item.vehicle_no,
          type: item.vehicle_type,
          desc: item.desc,
          capacity: item.max_capacity,
        }));
      }
      return [];
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      return [];
    }
  }, []);

  useEffect(() => {
    const uniqueTransporters = [
      ...new Set(job?.legs.map((leg) => leg.transporter.code).filter(Boolean)),
    ];

    uniqueTransporters.forEach(async (transporterCode) => {
      if (!vehiclesByTransporter[transporterCode]) {
        const vehicles = await getVehiclesForTransporter(transporterCode);
        setVehiclesByTransporter((prev) => ({
          ...prev,
          [transporterCode]: vehicles,
        }));
      }
    });
  }, [job, getVehiclesForTransporter, vehiclesByTransporter]);

  const get_job_details = async () => {
    try {
      setLoading(true);
      const url = new URL(API_BASE_URL + API_ENDPOINTS.JOBSDETAILS + jobId);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      let res = await response.json();
      console.log(`Response from API:`, res.data);

      if (res.status === 200) {
        let jobDtl = res.data;

        let legDtl = jobDtl.rt_legs.map((leg, index) => {
          return {
            id: index + 1,
            legId: leg.leg_id,
            expanded: false,
            type: leg.leg_type,
            legNumber: `LEG-${String(index + 1).padStart(3, "0")}`,

            // Stakeholder Information
            customer: {
              name: leg.rt_customer_master?.customer_name || "N/A",
              code: leg.rt_customer_master?.customer_code || "N/A",
            },
            vendor: {
              name: leg.rt_vendor_master?.vendor_name || "N/A",
              code: leg.rt_vendor_master?.vendor_code || "N/A",
            },
            transporter: {
              name: leg.rt_transpoter_master?.name || "N/A",
              code: leg.rt_transpoter_master?.transpoter_code || "N/A",
            },

            // Vehicle Information
            vehicle: {
              id: leg.tr_vehicle_master?.vehicle_id,
              number: leg.tr_vehicle_master?.vehicle_no || "N/A",
              type: leg.tr_vehicle_master?.vehicle_type || "N/A",
              capacity: leg.tr_vehicle_master?.max_capacity || "N/A",
              description: leg.tr_vehicle_master?.desc || "N/A",
              regNo: leg.tr_vehicle_master?.reg_no || "N/A",
              gpsNo: leg.tr_vehicle_master?.gps_no || "N/A",
              vehicleName: leg.tr_vehicle_master?.vehicle_name || "N/A",
              status:
                leg.tr_vehicle_master?.status === 1 ? "Active" : "Inactive",
            },

            // Driver Information
            driver: {
              name: leg.driver_name || "N/A",
              phone: leg.driver_contact_no || "N/A",
              license: "N/A", // Not available in API
              experience: "N/A", // Not available in API
              rating: 0, // Not available in API
              status: leg.status === 2 ? "Active" : "Inactive",
            },

            // Container Information
            container: {
              id: leg.rt_container_master?.container_id,
              code: leg.rt_container_master?.container_code || "N/A",
              company: leg.rt_container_master?.company_name || "N/A",
              size: leg.rt_container_master?.size || "N/A",
              type: leg.rt_container_master?.container_type || "N/A",
              capacity: leg.rt_container_master?.max_capacity || "N/A",
              regNo: leg.rt_container_master?.reg_no || "N/A",
              pocName: leg.rt_container_master?.poc_name || "N/A",
              pocNo: leg.rt_container_master?.poc_no || "N/A",
              freeTime: leg.rt_container_master?.free_time || "N/A",
              detentionRate: leg.rt_container_master?.detention_rate || "N/A",
              location: leg.rt_container_master?.location || "N/A",
            },

            // Warehouse Information
            warehouse: {
              id: leg.rt_wh_master?.wh_id,
              name: leg.rt_wh_master?.wh_name || "N/A",
            },

            // Goods Information
            goods: {
              id: leg.rt_goods_master?.goods_id,
              name: leg.rt_goods_master?.goods_name || "N/A",
            },

            // Location Information
            origin: leg.origin || "N/A",
            destination: leg.destination || "N/A",
            originCoords: leg.originPlaceData
              ? {
                  lat: leg.originPlaceData.lat,
                  lng: leg.originPlaceData.lng,
                }
              : null,
            destinationCoords: leg.destinationPlaceData
              ? {
                  lat: leg.destinationPlaceData.lat,
                  lng: leg.destinationPlaceData.lng,
                }
              : null,

            // Distance and Time
            distance: parseFloat(leg.distance) || 0,
            estimatedHours: parseFloat(leg.estimatedHours) || 0,

            // Dates
            startDate: leg.start_date,
            endDate: leg.end_date,
            approxStartDate: leg.approx_start_date,
            approxEndDate: leg.aprox_end_date,

            // Other Details
            weight: leg.weight || "0",
            specialInstructions: leg.remarks || "N/A",
            status:
              leg.status === 2
                ? "Active"
                : leg.status === 1
                  ? "Created"
                  : "Inactive",

            // Tracking
            trackingDetail: leg.rt_leg_vehicle_tracking,
            lastAddress: leg.last_address,
            lat: leg.lat,
            long: leg.long,

            // Status History
            statusHistory: leg.rt_leg_status_histories || [],

            // Progress calculation (basic)
            progress: leg.status === 2 ? 10 : leg.status === 1 ? 5 : 0,
          };
        });

        // Job details
        let jobDetails = {
          id: jobDtl.job_id,
          jobNumber: `${jobDtl.job_code}/${jobDtl.job_id}`,
          customerPO: jobDtl.customer_po || "N/A",
          tallySheetNo: jobDtl.tally_no || "N/A",
          description: jobDtl.job_desc || "No description available",
          type: jobDtl.job_type || "N/A",
          status: jobDtl.status === 1 ? "Active" : "Inactive",
          statusName: jobDtl.rt_status_master?.status_name || "Unknown",
          statusCode: jobDtl.rt_status_master?.status_code || "N/A",
          priority: jobDtl.priority || "Medium",

          // Dates
          createdAt: jobDtl.created_at,
          updatedAt: jobDtl.updated_at,
          createdBy: jobDtl.created_by,
          updatedBy: jobDtl.updated_by,
          jobCreateDate: jobDtl.job_create_date,
          jobEndDate: jobDtl.job_end_date,
          jobActualStartDate: jobDtl.job_actual_start_date,
          jobActualEndDate: jobDtl.job_actual_end_date,

          // Metrics
          totalDistance: parseFloat(jobDtl.total_distance) || 0,
          totalLegs: jobDtl.total_legs || legDtl.length,
          totalDuration: parseFloat(jobDtl.total_duration) || 0,

          // Legs
          legs: legDtl,

          // Route summary
          route: {
            totalDistance: parseFloat(jobDtl.total_distance) || 0,
            estimatedDuration: parseFloat(jobDtl.total_duration) || 0,
            actualDuration: 0, // Not available in API
            currentLeg: 1, // Default to first leg
            efficiency: 0, // Not available in API
          },

          // Progress calculation
          overallProgress:
            legDtl.length > 0
              ? Math.round(
                  legDtl.reduce((sum, leg) => sum + leg.progress, 0) /
                    legDtl.length
                )
              : 0,
        };

        setJob(jobDetails);
        setLastRefresh(new Date());
      }
    } catch (error) {
      console.error(`Error fetching job details:`, error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLegStatuses = async () => {
    try {
      const url1 = new URL(API_BASE_URL + API_ENDPOINTS.LEGSTATUSMASTER);
      const response = await fetch(url1, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      let res = await response.json();
      console.log(`Response from API:`, res.data);

      if (res.status === 200) {
        console.log(`Response from API:`, res.data);
        setLegStatuses(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch leg statuses", error);
    }
  };

  const fetchJobStatuses = async () => {
    try {
      const url = new URL(API_BASE_URL + API_ENDPOINTS.JOBSTATUSMASTER);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      let res = await response.json();
      console.log("Job statuses response:", res.data);

      if (res.status === 200) {
        setJobStatuses(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch job statuses", error);
    }
  };

  const updateLegStatus = async (legId, statusId) => {
    try {
      setUpdatingLegStatus(true);

      const postData = {
        leg_id: legId,
        status: statusId,
        updated_by: localStorage.getItem("userEmail") || "admin",
      };

      const url = new URL(API_BASE_URL + API_ENDPOINTS.LEGSTATUSUPDATE);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(postData),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const res = await response.json();
      console.log("Leg status update response:", res);

      if (res.status === 200) {
        setNotification({
          type: "success",
          title: "Status Updated",
          message: "Leg status updated successfully!",
        });
        await get_job_details();
      } else {
        throw new Error(res.message || "Failed to update leg status");
      }
    } catch (error) {
      console.error("Error updating leg status:", error);
      setNotification({
        type: "error",
        title: "Update Failed",
        message: "Failed to update leg status. Please try again.",
      });
    } finally {
      setUpdatingLegStatus(false);
    }
  };

  const updateJobStatus = async (jobId, statusId) => {
    try {
      setUpdatingJobStatus(true);

      const postData = {
        job_id: jobId,
        status: statusId,
        updated_by: localStorage.getItem("userEmail") || "admin",
      };

      const url = new URL(API_BASE_URL + API_ENDPOINTS.JOBSTATUSUPDATE);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(postData),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const res = await response.json();
      console.log("Job status update response:", res);

      if (res.status === 200) {
        setNotification({
          type: "success",
          title: "Status Updated",
          message: "Job status updated successfully!",
        });
        await get_job_details();
      } else {
        throw new Error(res.message || "Failed to update job status");
      }
    } catch (error) {
      console.error("Error updating job status:", error);
      setNotification({
        type: "error",
        title: "Update Failed",
        message: "Failed to update job status. Please try again.",
      });
    } finally {
      setUpdatingJobStatus(false);
    }
  };

  const StatusUpdateModal = () => {
    const [selectedStatus, setSelectedStatus] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleStatusUpdate = async () => {
      if (!selectedStatus) return;

      setIsLoading(true);
      if (statusModalType === "job") {
        await updateJobStatus(selectedStatusItem.id, parseInt(selectedStatus));
      } else if (statusModalType === "leg") {
        await updateLegStatus(
          selectedStatusItem.legId,
          parseInt(selectedStatus)
        );
      }
      setIsLoading(false);
      setShowStatusModal(false);
      setSelectedStatus("");
    };

    const statusOptions = statusModalType === "job" ? jobStatuses : legStatuses;
    const currentStatusName =
      statusModalType === "job"
        ? selectedStatusItem?.statusName
        : selectedStatusItem?.status;

    return (
      <Modal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedStatus("");
        }}
        title={`Update ${statusModalType === "job" ? "Job" : "Leg"} Status`}
        size="md"
      >
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">
              {statusModalType === "job"
                ? "Job Information"
                : "Leg Information"}
            </h4>
            <div className="text-sm text-gray-600">
              {statusModalType === "job" ? (
                <>
                  <p>
                    <strong>Job Number:</strong> {selectedStatusItem?.jobNumber}
                  </p>
                  <p>
                    <strong>Current Status:</strong> {currentStatusName}
                  </p>
                  <p>
                    <strong>Priority:</strong> {selectedStatusItem?.priority}
                  </p>
                </>
              ) : (
                <>
                  <p>
                    <strong>Leg:</strong> {selectedStatusItem?.legNumber}
                  </p>
                  <p>
                    <strong>Route:</strong> {selectedStatusItem?.origin} →{" "}
                    {selectedStatusItem?.destination}
                  </p>
                  <p>
                    <strong>Current Status:</strong> {currentStatusName}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Select New Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Choose new status...</option>
              {statusOptions.map((status) => (
                <option key={status.status_id} value={status.status_id}>
                  {status.status_name}
                </option>
              ))}
            </select>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Important Notice</p>
                <p>
                  Status changes are immediate and cannot be undone. Please
                  confirm you want to proceed.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowStatusModal(false);
                setSelectedStatus("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleStatusUpdate}
              disabled={!selectedStatus || isLoading}
              loading={isLoading}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Update Status
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const AddLegModal = () => {
    const [newLeg, setNewLeg] = useState({
      type: "pickup",
      customer: "",
      vendor: "",
      port: "",
      warehouse: "",
      transporter: "",
      vehicle: "",
      driverName: "",
      driverContact: "",
      origin: "",
      destination: "",
      originPlaceData: null,
      destinationPlaceData: null,
      goodsType: "",
      container: "",
      distance: 0,
      estimatedHours: 0,
      journeyDate: "",
      journeyTime: "",
      specialInstructions: "",
      weight: "0",
    });

    const [isLoading, setIsLoading] = useState(false);

    const updateNewLeg = (field, value, additionalData = null) => {
      setNewLeg((prev) => {
        const updated = { ...prev, [field]: value };

        if (field === "transporter") {
          updated.vehicle = "";
        } else if (field === "distance" && value > 0) {
          updated.estimatedHours = Math.round((value / 40) * 10) / 10;
        } else if (field === "origin" && additionalData) {
          updated.originPlaceData = additionalData;
          if (
            prev.destinationPlaceData &&
            additionalData.lat &&
            additionalData.lng
          ) {
            const distance = calculateDistance(
              additionalData.lat,
              additionalData.lng,
              prev.destinationPlaceData.lat,
              prev.destinationPlaceData.lng
            );
            updated.distance = distance;
            updated.estimatedHours = Math.round((distance / 40) * 10) / 10;
          }
        } else if (field === "destination" && additionalData) {
          updated.destinationPlaceData = additionalData;
          if (
            prev.originPlaceData &&
            additionalData.lat &&
            additionalData.lng
          ) {
            const distance = calculateDistance(
              prev.originPlaceData.lat,
              prev.originPlaceData.lng,
              additionalData.lat,
              additionalData.lng
            );
            updated.distance = distance;
            updated.estimatedHours = Math.round((distance / 40) * 10) / 10;
          }
        }

        return updated;
      });
    };

    useEffect(() => {
      if (newLeg.transporter && !vehiclesByTransporter[newLeg.transporter]) {
        getVehiclesForTransporter(newLeg.transporter).then((vehicles) => {
          setVehiclesByTransporter((prev) => ({
            ...prev,
            [newLeg.transporter]: vehicles,
          }));
        });
      }
    }, [newLeg.transporter, vehiclesByTransporter, getVehiclesForTransporter]);

    const handleAddLeg = async () => {
      setIsLoading(true);
      try {
        const selectedVehicle = (
          vehiclesByTransporter[newLeg.transporter] || []
        ).find((v) => v.value === newLeg.vehicle);

        const legPayload = {
          vehicle_id: parseInt(newLeg.vehicle) || null,
          container_id: parseInt(newLeg.container) || null,
          goods_id: newLeg.goodsType || 0,
          weight: newLeg.weight || "0",
          start_date: newLeg.journeyDate,
          // end_date: "2025-01-01",
          status: 1,
          vendor_code: newLeg.vendor,
          transpoter_code: newLeg.transporter,
          driver_contact_no: newLeg.driverContact,
          driver_name: newLeg.driverName,
          created_by: "admin",
          leg_type: newLeg.type,
          vehicle_type: selectedVehicle?.type || "",
          port_id: parseInt(newLeg.port) || null,
          origin: newLeg.origin,
          destination: newLeg.destination,
          remarks: newLeg.specialInstructions || "remarks",
          customer_code: newLeg.customer,
          wh_id: parseInt(newLeg.warehouse) || null,
          originPlaceData: newLeg.originPlaceData,
          destinationPlaceData: newLeg.destinationPlaceData,
          distance: newLeg.distance,
          estimatedHours: newLeg.estimatedHours,
          job_id: job.id,
        };

        const response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.LEGSCREATE}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
            body: JSON.stringify(legPayload),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const res = await response.json();
        if (res.status === 201) {
          setNotification({
            type: "success",
            title: "Leg Added",
            message: "New leg has been added to the job successfully!",
          });
          await get_job_details();
          setShowAddLegModal(false);
          // Reset form
          setNewLeg({
            type: "pickup",
            customer: "",
            vendor: "",
            port: "",
            warehouse: "",
            transporter: "",
            vehicle: "",
            driverName: "",
            driverContact: "",
            origin: "",
            destination: "",
            originPlaceData: null,
            destinationPlaceData: null,
            goodsType: "",
            container: "",
            distance: 0,
            estimatedHours: 0,
            journeyDate: "",
            journeyTime: "",
            specialInstructions: "",
            weight: "0",
          });
        } else {
          throw new Error(res.message || "Failed to add leg");
        }
      } catch (error) {
        console.error("Error adding leg:", error);
        setNotification({
          type: "error",
          title: "Failed to Add Leg",
          message: "Unable to add new leg. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <Modal
        isOpen={showAddLegModal}
        onClose={() => setShowAddLegModal(false)}
        title="Add New Leg to Job"
        size="xl"
      >
        <div className="space-y-6 max-h-96 overflow-y-auto">
          {/* Basic Information */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className={labelClassName}>Leg Type</label>
                <select
                  value={newLeg.type}
                  onChange={(e) => updateNewLeg("type", e.target.value)}
                  className={inputClassName}
                >
                  <option value="pickup">Pickup</option>
                  <option value="delivery">Delivery</option>
                  <option value="transit">Transit</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Goods Type</label>
                <select
                  value={newLeg.goodsType}
                  onChange={(e) => updateNewLeg("goodsType", e.target.value)}
                  className={inputClassName}
                >
                  <option value="">Select Goods Type</option>
                  {goodsTypes.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Weight (kg)</label>
                <input
                  type="number"
                  value={newLeg.weight}
                  onChange={(e) => updateNewLeg("weight", e.target.value)}
                  placeholder="0"
                  className={inputClassName}
                />
              </div>
            </div>
          </div>

          {/* Route Information */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">
              Route Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {googleMapsLoaded ? (
                <GooglePlacesAutocomplete
                  label="Origin"
                  value={newLeg.origin}
                  onChange={(e, placeData) =>
                    updateNewLeg("origin", e.target.value, placeData)
                  }
                  placeholder="Enter pickup location"
                  required
                />
              ) : (
                <div className="space-y-1">
                  <label className={labelClassName}>Origin *</label>
                  <input
                    type="text"
                    value={newLeg.origin}
                    onChange={(e) => updateNewLeg("origin", e.target.value)}
                    placeholder="Enter pickup location"
                    className={inputClassName}
                  />
                </div>
              )}

              {googleMapsLoaded ? (
                <GooglePlacesAutocomplete
                  label="Destination"
                  value={newLeg.destination}
                  onChange={(e, placeData) =>
                    updateNewLeg("destination", e.target.value, placeData)
                  }
                  placeholder="Enter delivery location"
                  required
                />
              ) : (
                <div className="space-y-1">
                  <label className={labelClassName}>Destination *</label>
                  <input
                    type="text"
                    value={newLeg.destination}
                    onChange={(e) =>
                      updateNewLeg("destination", e.target.value)
                    }
                    placeholder="Enter delivery location"
                    className={inputClassName}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <label className={labelClassName}>Distance (km)</label>
                <input
                  type="number"
                  value={newLeg.distance}
                  onChange={(e) => updateNewLeg("distance", e.target.value)}
                  placeholder="0"
                  className={inputClassName}
                />
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Est. Duration</label>
                <div className="px-3 py-2 bg-blue-50 border border-blue-300 rounded-lg">
                  <span className="text-blue-800 font-medium flex items-center">
                    <Timer className="w-4 h-4 mr-2" />
                    {newLeg.estimatedHours} hours
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Journey Date</label>
                <input
                  type="date"
                  value={newLeg.journeyDate}
                  onChange={(e) => updateNewLeg("journeyDate", e.target.value)}
                  className={inputClassName}
                />
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Journey Time</label>
                <input
                  type="time"
                  value={newLeg.journeyTime}
                  onChange={(e) => updateNewLeg("journeyTime", e.target.value)}
                  className={inputClassName}
                />
              </div>
            </div>
          </div>

          {/* Stakeholders */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Stakeholders</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-1">
                <label className={labelClassName}>Customer</label>
                <select
                  value={newLeg.customer}
                  onChange={(e) => updateNewLeg("customer", e.target.value)}
                  className={inputClassName}
                >
                  <option value="">Select Customer</option>
                  {customers.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Vendor</label>
                <select
                  value={newLeg.vendor}
                  onChange={(e) => updateNewLeg("vendor", e.target.value)}
                  className={inputClassName}
                >
                  <option value="">Select Vendor</option>
                  {vendors.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Port</label>
                <select
                  value={newLeg.port}
                  onChange={(e) => updateNewLeg("port", e.target.value)}
                  className={inputClassName}
                >
                  <option value="">Select Port</option>
                  {ports.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Warehouse</label>
                <select
                  value={newLeg.warehouse}
                  onChange={(e) => updateNewLeg("warehouse", e.target.value)}
                  className={inputClassName}
                >
                  <option value="">Select Warehouse</option>
                  {warehouses.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Transport Details */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">
              Transport Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className={labelClassName}>Transporter</label>
                <select
                  value={newLeg.transporter}
                  onChange={(e) => updateNewLeg("transporter", e.target.value)}
                  className={inputClassName}
                >
                  <option value="">Select Transporter</option>
                  {transporters.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Vehicle</label>
                <select
                  value={newLeg.vehicle}
                  onChange={(e) => updateNewLeg("vehicle", e.target.value)}
                  disabled={!newLeg.transporter}
                  className={inputClassName}
                >
                  <option value="">Select Vehicle</option>
                  {(vehiclesByTransporter[newLeg.transporter] || []).map(
                    (v) => (
                      <option key={v.value} value={v.value}>
                        {v.label}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Container</label>
                <select
                  value={newLeg.container}
                  onChange={(e) => updateNewLeg("container", e.target.value)}
                  className={inputClassName}
                >
                  <option value="">Select Container</option>
                  {containers.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label} ({c.type.toUpperCase()}) - {c.owner}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-1">
                <label className={labelClassName}>Driver Name</label>
                <input
                  type="text"
                  value={newLeg.driverName}
                  onChange={(e) => updateNewLeg("driverName", e.target.value)}
                  placeholder="Enter driver name"
                  className={inputClassName}
                />
              </div>

              <div className="space-y-1">
                <label className={labelClassName}>Driver Contact</label>
                <input
                  type="text"
                  value={newLeg.driverContact}
                  onChange={(e) =>
                    updateNewLeg("driverContact", e.target.value)
                  }
                  placeholder="Enter driver contact number"
                  className={inputClassName}
                />
              </div>
            </div>
          </div>

          {/* Special Instructions */}
          <div className="space-y-1">
            <label className={labelClassName}>Special Instructions</label>
            <input
              type="text"
              value={newLeg.specialInstructions}
              onChange={(e) =>
                updateNewLeg("specialInstructions", e.target.value)
              }
              placeholder="Enter any special handling instructions for this leg..."
              className={inputClassName}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t">
          <Button variant="outline" onClick={() => setShowAddLegModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddLeg}
            disabled={!newLeg.origin || !newLeg.destination || isLoading}
            loading={isLoading}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Leg
          </Button>
        </div>
      </Modal>
    );
  };

  const TollTrackerModal = () => {
    const selectedLeg = selectedLegForTollTracker
      ? job.legs.find((l) => l.legId === selectedLegForTollTracker)
      : null;

    return (
      <Modal
        isOpen={showTollTrackerModal}
        onClose={() => {
          setShowTollTrackerModal(false);
          setSelectedLegForTollTracker(null);
        }}
        title="Route Toll & Weather Tracker"
        size="xl"
      >
        <TollWeatherTracker
          leg={selectedLeg}
          onClose={() => {
            setShowTollTrackerModal(false);
            setSelectedLegForTollTracker(null);
          }}
        />
      </Modal>
    );
  };

  // Editable Field Component
  const EditableField = ({
    label,
    value,
    field,
    type = "text",
    options = null,
    onSave,
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);

    const handleSave = () => {
      onSave(field, editValue);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditValue(value);
      setIsEditing(false);
    };

    if (isEditing) {
      return (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">{label}</label>
          <div className="flex items-center space-x-2">
            {type === "select" ? (
              <select
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={type}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            )}
            <Button variant="primary" size="sm" onClick={handleSave}>
              <Save className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleCancel}>
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <div className="flex items-center justify-between group">
          <span className="text-gray-900">{value}</span>
          {editMode && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Edit className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Modal Component
  const Modal = ({ isOpen, onClose, title, children, size = "md" }) => {
    if (!isOpen) return null;

    const sizes = {
      sm: "max-w-md",
      md: "max-w-lg",
      lg: "max-w-2xl",
      xl: "max-w-4xl",
    };

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={onClose}
          ></div>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
            &#8203;
          </span>

          <div
            className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full ${sizes[size]}`}
          >
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <span className="sr-only">Close</span>✕
                </button>
              </div>
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Notification = ({ notification, onClose }) => {
    useEffect(() => {
      if (notification) {
        const timer = setTimeout(() => {
          onClose();
        }, 5000);
        return () => clearTimeout(timer);
      }
    }, [notification, onClose]);

    if (!notification) return null;

    const bgColor =
      notification.type === "success"
        ? "bg-green-50 border-green-200"
        : notification.type === "error"
          ? "bg-red-50 border-red-200"
          : "bg-blue-50 border-blue-200";
    const textColor =
      notification.type === "success"
        ? "text-green-800"
        : notification.type === "error"
          ? "text-red-800"
          : "text-blue-800";
    const iconColor =
      notification.type === "success"
        ? "text-green-600"
        : notification.type === "error"
          ? "text-red-600"
          : "text-blue-600";

    return (
      <div className="fixed top-4 right-4 z-50 max-w-md">
        <div
          className={`${bgColor} border rounded-lg shadow-lg p-4 flex items-start space-x-3`}
        >
          <div className={`${iconColor} mt-0.5`}>
            {notification.type === "success" ? (
              <CheckCircle className="w-5 h-5" />
            ) : notification.type === "error" ? (
              <AlertTriangle className="w-5 h-5" />
            ) : (
              <Info className="w-5 h-5" />
            )}
          </div>
          <div className="flex-1">
            <h4 className={`font-medium ${textColor}`}>{notification.title}</h4>
            <p className={`text-sm ${textColor} mt-1`}>
              {notification.message}
            </p>
          </div>
          <button onClick={onClose} className={`${textColor} hover:opacity-70`}>
            <span className="sr-only">Close</span>✕
          </button>
        </div>
      </div>
    );
  };

  // Form handlers
  const handleFieldSave = (field, value) => {
    console.log(`Saving ${field}: ${value}`);
    setJob((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLegFieldSave = (legId, field, value) => {
    console.log(`Saving leg ${legId} ${field}: ${value}`);
    setJob((prev) => ({
      ...prev,
      legs: prev.legs.map((leg) =>
        leg.legId === legId ? { ...leg, [field]: value } : leg
      ),
    }));
  };

  // Modal handlers
  const handleVehicleUpdate = async (legId, vehicleData) => {
    try {
      let postData = {
        leg_id: legId,
        driver_name: job.legs.find((l) => l.legId === legId)?.driver.name,
        driver_contact_no: job.legs.find((l) => l.legId === legId)?.driver
          .phone,
        vehicle_id: vehicleData.vehicle_id,
        updated_by: "admin",
      };

      const url = new URL(API_BASE_URL + API_ENDPOINTS.UPDATEVEHICLEDRIVER);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(postData),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      let res = await response.json();
      console.log("Vehicle update response:", res);

      setNotification({
        type: "success",
        title: "Vehicle Updated",
        message: "Vehicle assignment updated successfully!",
      });

      await get_job_details();
      setShowVehicleModal(false);
    } catch (error) {
      console.error("Error updating vehicle:", error);
      setNotification({
        type: "error",
        title: "Update Failed",
        message: "Failed to update vehicle assignment. Please try again.",
      });
    }
  };

  const handleDriverUpdate = async (legId, driverData) => {
    try {
      let postData = {
        leg_id: legId,
        driver_name: driverData.name,
        driver_contact_no: driverData.phone,
        vehicle_id: job.legs.find((l) => l.legId === legId)?.vehicle.id,
        updated_by: "admin",
      };

      const url = new URL(API_BASE_URL + API_ENDPOINTS.UPDATEVEHICLEDRIVER);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(postData),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      let res = await response.json();
      console.log("Driver update response:", res);

      setNotification({
        type: "success",
        title: "Driver Updated",
        message: "Driver assignment updated successfully!",
      });

      await get_job_details();
      setShowDriverModal(false);
    } catch (error) {
      console.error("Error updating driver:", error);
      setNotification({
        type: "error",
        title: "Update Failed",
        message: "Failed to update driver assignment. Please try again.",
      });
    }
  };

  // Modal Components
  const VehicleUpdateModal = () => {
    const [vehicleData, setVehicleData] = useState({
      vehicle_id: selectedLegForUpdate
        ? job.legs.find((l) => l.legId === selectedLegForUpdate)?.vehicle.id ||
          ""
        : "",
      number: selectedLegForUpdate
        ? job.legs.find((l) => l.legId === selectedLegForUpdate)?.vehicle
            .number || ""
        : "",
      type: selectedLegForUpdate
        ? job.legs.find((l) => l.legId === selectedLegForUpdate)?.vehicle
            .type || ""
        : "",
    });

    return (
      <Modal
        isOpen={showVehicleModal}
        onClose={() => setShowVehicleModal(false)}
        title="Update Vehicle Assignment"
        size="lg"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle ID
              </label>
              <input
                type="number"
                value={vehicleData.vehicle_id}
                onChange={(e) =>
                  setVehicleData({ ...vehicleData, vehicle_id: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter vehicle ID"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Vehicle
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                <span className="text-gray-900 font-medium">
                  {vehicleData.number} ({vehicleData.type})
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowVehicleModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                handleVehicleUpdate(selectedLegForUpdate, vehicleData)
              }
            >
              Update Vehicle
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const DriverUpdateModal = () => {
    const [driverData, setDriverData] = useState({
      name: selectedLegForUpdate
        ? job.legs.find((l) => l.legId === selectedLegForUpdate)?.driver.name ||
          ""
        : "",
      phone: selectedLegForUpdate
        ? job.legs.find((l) => l.legId === selectedLegForUpdate)?.driver
            .phone || ""
        : "",
    });

    return (
      <Modal
        isOpen={showDriverModal}
        onClose={() => setShowDriverModal(false)}
        title="Update Driver Assignment"
        size="lg"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Driver Name
              </label>
              <input
                type="text"
                value={driverData.name}
                onChange={(e) =>
                  setDriverData({ ...driverData, name: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter driver name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={driverData.phone}
                onChange={(e) =>
                  setDriverData({ ...driverData, phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="+91-9876543210"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDriverModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                handleDriverUpdate(selectedLegForUpdate, driverData)
              }
            >
              Update Driver
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: Eye },
    { id: "legs", label: "Legs Management", icon: Map },
    { id: "timeline", label: "Progress Timeline", icon: Activity },
    { id: "alerts", label: "Alerts & Monitoring", icon: Bell },
    { id: "documents", label: "Documents", icon: FileText },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-lg font-medium text-gray-700">
            Loading job details...
          </span>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-2xl font-bold text-gray-900">Job Not Found</h2>
        <p className="text-gray-600 mt-2">
          The requested job could not be found.
        </p>
        <Button
          variant="primary"
          onClick={() => window.history.back()}
          className="mt-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Jobs
        </Button>
      </div>
    );
  }

  // Tab Content Components
  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-600">Overall Progress</p>
              <p className="text-lg font-bold text-gray-900">
                {job.overallProgress}%
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Ruler className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-600">Total Distance</p>
              <p className="text-lg font-bold text-gray-900">
                {job.totalDistance} km
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Timer className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-600">Est. Duration</p>
              <p className="text-lg font-bold text-gray-900">
                {parseFloat(job.totalDuration).toFixed(2)} hours
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Map className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-600">Total Legs</p>
              <p className="text-lg font-bold text-gray-900">{job.totalLegs}</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Job Information */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Job Information
              </h3>
              {/* <Button
                variant="outline"
                size="sm"
                onClick={() => setEditMode(!editMode)}
              >
                <Edit className="w-4 h-4 mr-2" />
                {editMode ? "Done Editing" : "Edit Details"}
              </Button> */}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <EditableField
                label="Job Number"
                value={job.jobNumber}
                field="jobNumber"
                onSave={handleFieldSave}
              />
              <EditableField
                label="Customer PO"
                value={job.customerPO}
                field="customerPO"
                onSave={handleFieldSave}
              />
              <EditableField
                label="Tally Sheet No"
                value={job.tallySheetNo}
                field="tallySheetNo"
                onSave={handleFieldSave}
              />
              <EditableField
                label="Priority"
                value={job.priority}
                field="priority"
                type="select"
                options={[
                  { value: "Low", label: "Low Priority" },
                  { value: "Medium", label: "Medium Priority" },
                  { value: "High", label: "High Priority" },
                ]}
                onSave={handleFieldSave}
              />
              <EditableField
                label="Job Type"
                value={job.type}
                field="type"
                onSave={handleFieldSave}
              />
              <EditableField
                label="Status"
                value={job.statusName}
                field="statusName"
                onSave={handleFieldSave}
              />
            </div>
            <div className="mt-4">
              <EditableField
                label="Description"
                value={job.description}
                field="description"
                onSave={handleFieldSave}
              />
            </div>

            {/* Dates Information */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Created At
                </label>
                <p className="text-gray-900">
                  {new Date(job.createdAt).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Created By
                </label>
                <p className="text-gray-900">{job.createdBy}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Job Create Date
                </label>
                <p className="text-gray-900">
                  {job.jobCreateDate
                    ? new Date(job.jobCreateDate).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
              {/* <div>
                <label className="text-sm font-medium text-gray-700">Job End Date</label>
                <p className="text-gray-900">{job.jobEndDate ? new Date(job.jobEndDate).toLocaleDateString() : 'N/A'}</p>
              </div> */}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          {/* <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Actions
            </h3>
            <div className="space-y-2"> */}
          {/* <Button
                variant="primary"
                className="w-full"
                onClick={() => get_job_details()}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Data
              </Button> */}
          {/* <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setSelectedLegForUpdate(job.legs[0]?.legId);
                  setShowPhotoModal(true);
                }}
              >
                <Camera className="w-4 h-4 mr-2" />
                Request Photo Update
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setSelectedLegForUpdate(job.legs[0]?.legId);
                  setShowIssueModal(true);
                }}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Report Issue
              </Button> */}
          {/* <Button variant="outline" className="w-full">
                <Share2 className="w-4 h-4 mr-2" />
                Share Details
              </Button> */}
          {/* </div>
          </Card> */}

          {/* Job Status */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Job Status Management
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => get_job_details()}
                loading={loading}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Current Status</span>
                <Badge
                  variant={job.status === "Active" ? "success" : "default"}
                >
                  {job.statusName}
                </Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Priority</span>
                <Badge
                  variant={
                    job.priority === "High"
                      ? "danger"
                      : job.priority === "Medium"
                        ? "warning"
                        : "default"
                  }
                >
                  {job.priority}
                </Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Last Updated</span>
                <span className="text-sm text-gray-900">
                  {lastRefresh.toLocaleTimeString()}
                </span>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setSelectedStatusItem(job);
                    setStatusModalType("job");
                    setShowStatusModal(true);
                  }}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Update Job Status
                </Button>
              </div>
            </div>
          </Card>

          {/* Route Summary */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Route Summary
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Distance</span>
                <span className="text-sm font-medium text-gray-900">
                  {job.totalDistance} km
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Est. Duration</span>
                <span className="text-sm font-medium text-gray-900">
                  {parseFloat(job.totalDuration).toFixed(2)} hours
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Legs</span>
                <span className="text-sm font-medium text-gray-900">
                  {job.totalLegs}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Current Leg</span>
                <span className="text-sm font-medium text-gray-900">
                  {job.route.currentLeg} of {job.totalLegs}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );

  const LegsTab = () => (
    <div className="space-y-6">
      {/* Legs Overview */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Map className="w-5 h-5 mr-2" />
            Route Legs Management ({job.legs.length} legs)
          </h3>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowAddLegModal(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Leg
          </Button>
        </div>

        <div className="space-y-6">
          {job.legs.map((leg, index) => (
            <div
              key={leg.legId}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Leg Header */}
              <div className="p-4 bg-gray-50 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                        leg.status === "Active"
                          ? "bg-green-100 text-green-600"
                          : leg.status === "Created"
                            ? "bg-blue-100 text-blue-600"
                            : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {leg.status === "Active" ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : leg.status === "Created" ? (
                        <Clock className="w-4 h-4" />
                      ) : (
                        <span className="text-sm font-medium">{index + 1}</span>
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {leg.legNumber}:{" "}
                        {leg.type.charAt(0).toUpperCase() + leg.type.slice(1)}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {leg.origin} → {leg.destination}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge
                      variant={
                        leg.status === "Active"
                          ? "success"
                          : leg.status === "Created"
                            ? "warning"
                            : "default"
                      }
                    >
                      {leg.status}
                    </Badge>

                    {/* Consent Status */}
                    <ConsentStatus contact={leg.driver.phone} />

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setSelectedLeg(
                          selectedLeg === leg.legId ? null : leg.legId
                        )
                      }
                    >
                      <ChevronRight
                        className={`w-4 h-4 transition-transform ${
                          selectedLeg === leg.legId ? "rotate-90" : ""
                        }`}
                      />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Expanded Leg Details */}
              {selectedLeg === leg.legId && (
                <div className="p-6 border-t bg-white">
                  {/* Progress Timeline for Each Leg */}
                  <div className="mt-6 space-y-6">
                    <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-4 mb-8">
                      <div className="flex items-center justify-between mb-4">
                        <h6 className="font-medium text-gray-900">
                          {leg.legNumber} Progress Tracker
                        </h6>
                        <Badge variant="blue" size="sm">
                          {leg.progress}% Complete
                        </Badge>
                      </div>

                      {/* Timeline Steps */}
                      <div className="relative">
                        {/* Progress Line */}
                        <div className="absolute top-6 left-6 right-6 h-1 bg-gray-200 rounded-full">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-1000"
                            style={{
                              width: `${leg.progress}%`,
                            }}
                          />
                        </div>

                        {/* Milestones */}
                        <div className="flex justify-between relative">
                          {[
                            {
                              id: 1,
                              name: "Vehicle Assigned",
                              status: leg.vehicle.id ? "completed" : "pending",
                              timestamp: leg.startDate,
                              location: "System",
                              notes: `Vehicle ${leg.vehicle.number} assigned`,
                            },
                            {
                              id: 2,
                              name: "Loading Started",
                              status:
                                leg.status === "Active"
                                  ? "in_progress"
                                  : "pending",
                              timestamp:
                                leg.status === "Active" ? leg.startDate : null,
                              location: leg.origin.split(",")[0],
                              notes:
                                leg.status === "Active"
                                  ? "Loading in progress"
                                  : "Awaiting loading start",
                            },
                            {
                              id: 3,
                              name: "Journey Started",
                              status:
                                leg.status === "Active" && leg.progress > 20
                                  ? "in_progress"
                                  : leg.status === "Active"
                                    ? "completed"
                                    : "pending",
                              timestamp:
                                leg.status === "Active" ? leg.startDate : null,
                              location: leg.origin.split(",")[0],
                              notes:
                                leg.status === "Active" && leg.progress > 20
                                  ? "Journey in progress"
                                  : leg.status === "Active"
                                    ? "Journey started"
                                    : "Awaiting journey start",
                            },
                            {
                              id: 4,
                              name: "Destination Reached",
                              status:
                                leg.progress >= 90 ? "completed" : "pending",
                              timestamp: leg.endDate,
                              location: leg.destination.split(",")[0],
                              notes:
                                leg.progress >= 90
                                  ? "Arrived at destination"
                                  : "En route to destination",
                            },
                            {
                              id: 5,
                              name: "Unloading Completed",
                              status:
                                leg.progress >= 95 ? "completed" : "pending",
                              timestamp: leg.endDate,
                              location: leg.destination.split(",")[0],
                              notes:
                                leg.progress >= 95
                                  ? "Unloading completed successfully"
                                  : "Pending unloading",
                            },
                            {
                              id: 6,
                              name: "Leg Completed",
                              status:
                                leg.progress >= 100 ? "completed" : "pending",
                              timestamp: leg.endDate,
                              location: leg.destination.split(",")[0],
                              notes:
                                leg.progress >= 100
                                  ? "Leg completed successfully"
                                  : "Pending completion",
                            },
                          ].map((milestone, index) => (
                            <div
                              key={milestone.id}
                              className="flex flex-col items-center"
                            >
                              {/* Icon */}
                              <div
                                className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-white shadow-lg ${
                                  milestone.status === "completed"
                                    ? "bg-green-500 text-white"
                                    : milestone.status === "in_progress"
                                      ? "bg-blue-500 text-white animate-pulse"
                                      : "bg-gray-300 text-gray-600"
                                }`}
                              >
                                {milestone.status === "completed" ? (
                                  <CheckCircle className="w-4 h-4" />
                                ) : milestone.status === "in_progress" ? (
                                  <Activity className="w-4 h-4" />
                                ) : (
                                  <span className="text-xs font-bold">
                                    {index + 1}
                                  </span>
                                )}
                              </div>

                              {/* Label */}
                              <div className="mt-3 text-center max-w-20">
                                <p className="text-xs font-medium text-gray-900 leading-tight">
                                  {milestone.name}
                                </p>
                                {milestone.timestamp && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(
                                      milestone.timestamp
                                    ).toLocaleDateString()}
                                  </p>
                                )}
                                {milestone.location && (
                                  <p className="text-xs text-blue-600 mt-1 truncate">
                                    {milestone.location}
                                  </p>
                                )}
                              </div>

                              {/* Status Indicator */}
                              {milestone.status === "in_progress" && (
                                <div className="absolute -bottom-2 w-2 h-2 bg-blue-500 rounded-full animate-ping"></div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Current Status Details */}
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Activity className="w-4 h-4 text-blue-600 mr-2" />
                            <span className="text-sm font-medium text-blue-900">
                              Current Status: {leg.status}
                            </span>
                          </div>
                          <Badge variant="blue" size="sm">
                            {leg.progress}% Complete
                          </Badge>
                        </div>
                        <p className="text-sm text-blue-700 mt-1">
                          Distance: {leg.distance}km • Estimated:{" "}
                          {leg.estimatedHours}h
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Left Column - Leg Information */}
                    <div className="space-y-6">
                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">
                          Leg Information
                        </h5>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Leg Number
                            </label>
                            <p className="text-gray-900 font-medium">
                              {leg.legNumber}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Type
                            </label>
                            <p className="text-gray-900">{leg.type}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Distance
                            </label>
                            <p className="text-gray-900">{leg.distance} km</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Estimated Duration
                            </label>
                            <p className="text-gray-900">
                              {leg.estimatedHours} hours
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">
                          Stakeholders
                        </h5>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Customer
                            </label>
                            <p className="text-gray-900">{leg.customer.name}</p>
                            <p className="text-sm text-gray-500">
                              Code: {leg.customer.code}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Vendor
                            </label>
                            <p className="text-gray-900">{leg.vendor.name}</p>
                            <p className="text-sm text-gray-500">
                              Code: {leg.vendor.code}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Transporter
                            </label>
                            <p className="text-gray-900">
                              {leg.transporter.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              Code: {leg.transporter.code}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">
                          Vehicle & Driver
                        </h5>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Vehicle
                            </label>
                            <div className="flex items-center justify-between mt-1">
                              <div>
                                <p className="text-gray-900 font-medium">
                                  {leg.vehicle.number}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {leg.vehicle.type} • {leg.vehicle.capacity}
                                </p>
                                <p className="text-xs text-gray-400">
                                  {leg.vehicle.description}
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedLegForUpdate(leg.legId);
                                  setShowVehicleModal(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Driver
                            </label>
                            <div className="flex items-center justify-between mt-1">
                              <div>
                                <p className="text-gray-900 font-medium">
                                  {leg.driver.name}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {leg.driver.phone}
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedLegForUpdate(leg.legId);
                                  setShowDriverModal(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">
                          Container Information
                        </h5>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Container Code
                            </label>
                            <p className="text-gray-900">
                              {leg.container.code}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Size/Type
                            </label>
                            <p className="text-gray-900">
                              {leg.container.size} - {leg.container.type}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Company
                            </label>
                            <p className="text-gray-900">
                              {leg.container.company}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Capacity
                            </label>
                            <p className="text-gray-900">
                              {leg.container.capacity}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              POC Contact
                            </label>
                            <p className="text-gray-900">
                              {leg.container.pocName}
                            </p>
                            <p className="text-sm text-gray-500">
                              {leg.container.pocNo}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Actions and Map */}
                    <div className="space-y-6">
                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">
                          Route Details
                        </h5>
                        <div className="space-y-3">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Origin
                            </label>
                            <p className="text-gray-900">{leg.origin}</p>
                            {leg.originCoords && (
                              <p className="text-xs text-gray-500">
                                Lat: {leg.originCoords.lat}, Lng:{" "}
                                {leg.originCoords.lng}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Destination
                            </label>
                            <p className="text-gray-900">{leg.destination}</p>
                            {leg.destinationCoords && (
                              <p className="text-xs text-gray-500">
                                Lat: {leg.destinationCoords.lat}, Lng:{" "}
                                {leg.destinationCoords.lng}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">
                          Dates & Timeline
                        </h5>
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Start Date
                            </label>
                            <p className="text-gray-900">
                              {leg.startDate
                                ? new Date(leg.startDate).toLocaleDateString(
                                    "en-GB"
                                  )
                                : "N/A"}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              End Date
                            </label>
                            <p className="text-gray-900">
                              {leg.endDate
                                ? new Date(leg.endDate).toLocaleDateString()
                                : "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">
                          Quick Actions
                        </h5>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              window.open(`tel:${leg.driver.phone}`)
                            }
                          >
                            <Phone className="w-4 h-4 mr-1" />
                            Call Driver
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => {
                              setSelectedStatusItem(leg);
                              setStatusModalType("leg");
                              setShowStatusModal(true);
                            }}
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Update Status
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            className="col-span-2"
                            onClick={() => {
                              setSelectedLegForTollTracker(leg.legId);
                              setShowTollTrackerModal(true);
                            }}
                          >
                            <Navigation2 className="w-4 h-4 mr-1" />
                            View Toll & Weather Tracker
                          </Button>
                        </div>
                      </div>

                      {leg.specialInstructions !== "N/A" && (
                        <div>
                          <h5 className="font-medium text-gray-900 mb-3">
                            Special Instructions
                          </h5>
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-yellow-800 text-sm">
                              {leg.specialInstructions}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Additional Information */}
                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">
                          Additional Details
                        </h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Warehouse:</span>
                            <span className="text-gray-900">
                              {leg.warehouse.name}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Goods Type:</span>
                            <span className="text-gray-900">
                              {leg.goods.name}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Vehicle Status:
                            </span>
                            <Badge
                              variant={
                                leg.vehicle.status === "Active"
                                  ? "success"
                                  : "default"
                              }
                              size="sm"
                            >
                              {leg.vehicle.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Map Section */}
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-900">Live Tracking</h5>
                    <MapView
                      legId={leg.legId}
                      mapUrl={leg.trackingDetail?.trip_url}
                    />
                  </div>

                  {/* Documents Section for Each Leg */}
                  <div className="mt-6 space-y-4">
                    <h5 className="font-medium text-gray-900">Leg Documents</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h6 className="font-medium text-gray-900">
                            Pickup Documents
                          </h6>
                          <Badge variant="warning" size="sm">
                            Pending
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          Documents required for pickup at origin
                        </p>
                        <Button variant="outline" size="sm" className="w-full">
                          <Upload className="w-4 h-4 mr-1" />
                          Upload
                        </Button>
                      </Card>

                      <Card className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h6 className="font-medium text-gray-900">
                            Transit Photos
                          </h6>
                          <Badge variant="blue" size="sm">
                            Auto
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          Real-time photos during transit
                        </p>
                        <Button variant="outline" size="sm" className="w-full">
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </Card>

                      <Card className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h6 className="font-medium text-gray-900">
                            Delivery Proof
                          </h6>
                          <Badge variant="warning" size="sm">
                            Pending
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          Proof of delivery at destination
                        </p>
                        <Button variant="outline" size="sm" className="w-full">
                          <Upload className="w-4 h-4 mr-1" />
                          Upload
                        </Button>
                      </Card>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const TimelineTab = () => (
    <div className="space-y-6">
      {/* Overall Job Progress */}
      <Card className="p-6">
        <div className="mb-8 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900">
              Overall Job Progress
            </h4>
            <Badge variant="blue">{job.overallProgress}% Complete</Badge>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-1000"
              style={{ width: `${job.overallProgress}%` }}
            />
          </div>
          <div className="flex justify-between text-sm text-gray-600 mt-2">
            <span>Started: {new Date(job.createdAt).toLocaleDateString()}</span>
            <span>
              Type: {job.type} • Status: {job.statusName}
            </span>
            <span>
              ETA:{" "}
              {job.jobEndDate
                ? new Date(job.jobEndDate).toLocaleDateString()
                : "TBD"}
            </span>
          </div>
        </div>

        {/* Detailed Timeline per Leg */}
        <div className="space-y-8">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            Detailed Progress Timeline
          </h3>

          {job.legs.map((leg, legIndex) => (
            <div
              key={leg.legId}
              className="border border-gray-200 rounded-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h4 className="font-semibold text-gray-900">
                  {leg.legNumber}: {leg.origin.split(",")[0]} →{" "}
                  {leg.destination.split(",")[0]}
                </h4>
                <div className="flex items-center space-x-2">
                  <Badge
                    variant={
                      leg.status === "Active"
                        ? "success"
                        : leg.status === "Created"
                          ? "warning"
                          : "default"
                    }
                  >
                    {leg.status}
                  </Badge>
                  <Badge variant="blue" size="sm">
                    {leg.progress}% Complete
                  </Badge>
                </div>
              </div>

              {/* Leg Progress Timeline */}
              <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-4">
                <div className="relative">
                  <div className="absolute top-6 left-6 right-6 h-1 bg-gray-200 rounded-full">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-1000"
                      style={{ width: `${leg.progress}%` }}
                    />
                  </div>

                  <div className="flex justify-between relative">
                    {[
                      {
                        name: "Vehicle Assigned",
                        status: leg.vehicle.id ? "completed" : "pending",
                        notes: `Vehicle: ${leg.vehicle.number}`,
                      },
                      {
                        name: "Driver Assigned",
                        status:
                          leg.driver.name !== "N/A" ? "completed" : "pending",
                        notes: `Driver: ${leg.driver.name}`,
                      },
                      {
                        name: "Journey Started",
                        status:
                          leg.status === "Active" ? "in_progress" : "pending",
                        notes:
                          leg.status === "Active"
                            ? "Journey in progress"
                            : "Awaiting start",
                      },
                      {
                        name: "In Transit",
                        status:
                          leg.status === "Active" && leg.progress > 10
                            ? "in_progress"
                            : "pending",
                        notes: `${leg.progress}% complete`,
                      },
                      {
                        name: "Reached Destination",
                        status:
                          leg.status === "Reached" || leg.progress >= 100
                            ? "completed"
                            : "pending",
                        notes:
                          leg.status === "Reached"
                            ? "Arrived at destination"
                            : "En route",
                      },
                      {
                        name: "Completed",
                        status: leg.progress >= 100 ? "completed" : "pending",
                        notes:
                          leg.progress >= 100 ? "Leg completed" : "In progress",
                      },
                    ].map((milestone, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-white shadow-lg ${
                            milestone.status === "completed"
                              ? "bg-green-500 text-white"
                              : milestone.status === "in_progress"
                                ? "bg-blue-500 text-white animate-pulse"
                                : "bg-gray-300 text-gray-600"
                          }`}
                        >
                          {milestone.status === "completed" ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : milestone.status === "in_progress" ? (
                            <Activity className="w-4 h-4" />
                          ) : (
                            <span className="text-xs font-bold">
                              {index + 1}
                            </span>
                          )}
                        </div>
                        <div className="mt-3 text-center max-w-20">
                          <p className="text-xs font-medium text-gray-900 leading-tight">
                            {milestone.name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Activity className="w-4 h-4 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-blue-900">
                        Current: {leg.status} • {leg.progress}% Complete
                      </span>
                    </div>
                    <div className="text-xs text-blue-700">
                      {leg.distance}km • {leg.estimatedHours}h estimated
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const AlertsTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-600">Active Alerts</p>
              <p className="text-lg font-bold text-gray-900">0</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-600">Systems Online</p>
              <p className="text-lg font-bold text-gray-900">
                {job.legs.filter((l) => l.status === "Active").length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Navigation className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-600">GPS Tracking</p>
              <p className="text-lg font-bold text-gray-900">
                {job.legs.length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-600">Active Drivers</p>
              <p className="text-lg font-bold text-gray-900">
                {job.legs.filter((l) => l.driver.phone !== "N/A").length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Bell className="w-5 h-5 mr-2" />
          System Monitoring
        </h3>

        {job.legs.map((leg, index) => (
          <div
            key={leg.legId}
            className="p-4 border border-gray-200 rounded-lg mb-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div
                  className={`w-3 h-3 rounded-full mr-3 ${leg.status === "Active" ? "bg-green-500" : "bg-yellow-500"}`}
                ></div>
                <div>
                  <h5 className="font-medium text-gray-900">
                    {leg.legNumber} - {leg.vehicle.number}
                  </h5>
                  <p className="text-sm text-gray-600">
                    {leg.origin.split(",")[0]} → {leg.destination.split(",")[0]}
                  </p>
                </div>
              </div>
              <Badge variant={leg.status === "Active" ? "success" : "warning"}>
                {leg.status}
              </Badge>
            </div>
          </div>
        ))}

        <div className="text-center py-8">
          <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            All Systems Operational
          </h3>
          <p className="text-gray-600">
            No alerts or issues detected at this time.
          </p>
        </div>
      </Card>
    </div>
  );

  const DocumentsTab = () => (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Job Documents & Compliance
        </h3>
        <Button variant="primary" size="sm">
          <Upload className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Document Categories */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-gray-900">Job Documents</h4>
            <Badge variant="success" size="sm">
              Complete
            </Badge>
          </div>
          <p className="text-sm text-gray-600">Core job-related documents</p>
        </div>
        <div className="p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-gray-900">Leg Documents</h4>
            <Badge variant="warning" size="sm">
              {job.legs.length} Legs
            </Badge>
          </div>
          <p className="text-sm text-gray-600">
            Documents per leg/route segment
          </p>
        </div>
        <div className="p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-gray-900">Compliance</h4>
            <Badge variant="blue" size="sm">
              Tracking
            </Badge>
          </div>
          <p className="text-sm text-gray-600">
            Regulatory and compliance docs
          </p>
        </div>
      </div>

      {/* Job Level Documents */}
      <div className="space-y-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-4">
            Job Level Documents
          </h4>
          <div className="space-y-3">
            {[
              {
                name: "Job Creation Document",
                status: "available",
                type: "job",
                description: "Original job creation and approval document",
              },
              {
                name: "Customer Purchase Order",
                status: "available",
                type: "job",
                description: `Customer PO: ${job.customerPO}`,
              },
              {
                name: "Tally Sheet",
                status: "available",
                type: "job",
                description: `Tally No: ${job.tallySheetNo}`,
              },
              {
                name: "Job Completion Certificate",
                status: "pending",
                type: "job",
                description: "Final completion and sign-off document",
              },
            ].map((doc, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{doc.name}</h4>
                    <p className="text-sm text-gray-600">{doc.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={doc.status === "available" ? "success" : "warning"}
                  >
                    {doc.status}
                  </Badge>
                  {doc.status === "available" && (
                    <>
                      <Button variant="outline" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {doc.status === "pending" && (
                    <Button variant="primary" size="sm">
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Leg Specific Documents */}
        <div>
          <h4 className="font-medium text-gray-900 mb-4">
            Leg Specific Documents
          </h4>
          {job.legs.map((leg, legIndex) => (
            <div key={leg.legId} className="mb-6">
              <h5 className="font-medium text-gray-800 mb-3">
                {leg.legNumber}: {leg.origin} → {leg.destination}
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    name: "Pickup Authorization",
                    status: leg.status === "Active" ? "available" : "pending",
                    category: "pickup",
                  },
                  {
                    name: "Loading Photos",
                    status: "pending",
                    category: "photos",
                  },
                  {
                    name: "Transit Documentation",
                    status: "pending",
                    category: "transit",
                  },
                  {
                    name: "Delivery Receipt",
                    status: "pending",
                    category: "delivery",
                  },
                  {
                    name: "Unloading Photos",
                    status: "pending",
                    category: "photos",
                  },
                  {
                    name: "Proof of Delivery",
                    status: "pending",
                    category: "delivery",
                  },
                ].map((doc, docIndex) => (
                  <div
                    key={docIndex}
                    className="p-3 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h6 className="text-sm font-medium text-gray-900">
                        {doc.name}
                      </h6>
                      <Badge
                        variant={
                          doc.status === "available" ? "success" : "warning"
                        }
                        size="sm"
                      >
                        {doc.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-600 mb-2 capitalize">
                      {doc.category} document
                    </p>
                    <div className="flex gap-1">
                      {doc.status === "available" && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-xs"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            Download
                          </Button>
                        </>
                      )}
                      {doc.status === "pending" && (
                        <Button
                          variant="primary"
                          size="sm"
                          className="w-full text-xs"
                        >
                          <Upload className="w-3 h-3 mr-1" />
                          Upload
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );

  const PhotoRequestModal = () => {
    const [selectedLegId, setSelectedLegId] = useState(selectedLegForUpdate);
    const [photoType, setPhotoType] = useState("general");
    const [instructions, setInstructions] = useState("");

    const handlePhotoRequest = (legId) => {
      console.log("Requesting photo update for leg:", legId);
      // Here you would make an API call to request photo
      setNotification({
        type: "success",
        title: "Photo Request Sent",
        message: `Photo request sent for leg ${legId} successfully!`,
      });
      setShowPhotoModal(false);
    };

    return (
      <Modal
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        title="Request Photo Update"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Leg
            </label>
            <select
              value={selectedLegId}
              onChange={(e) => setSelectedLegId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {job.legs.map((leg, index) => (
                <option key={leg.legId} value={leg.legId}>
                  {leg.legNumber}: {leg.origin.split(",")[0]} →{" "}
                  {leg.destination.split(",")[0]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photo Type
            </label>
            <select
              value={photoType}
              onChange={(e) => setPhotoType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="general">General Vehicle Photo</option>
              <option value="cargo">Cargo Condition</option>
              <option value="loading">Loading/Unloading</option>
              <option value="location">Current Location</option>
              <option value="issue">Issue Documentation</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Special Instructions
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Any specific instructions for the photo..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowPhotoModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => handlePhotoRequest(selectedLegId)}
            >
              <Camera className="w-4 h-4 mr-2" />
              Request Photo
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  const IssueReportModal = () => {
    const [issueData, setIssueData] = useState({
      title: "",
      description: "",
      severity: "medium",
      category: "vehicle",
      legId: selectedLegForUpdate || job.legs[0]?.legId,
    });

    const handleIssueReport = (issueData) => {
      console.log("Reporting issue:", issueData);
      // Here you would make an API call to report the issue
      setNotification({
        type: "success",
        title: "Issue Reported",
        message: `Issue "${issueData.title}" has been reported for leg ${issueData.legId}`,
      });
      setShowIssueModal(false);
    };

    return (
      <Modal
        isOpen={showIssueModal}
        onClose={() => setShowIssueModal(false)}
        title="Report Issue"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Issue Title
            </label>
            <input
              type="text"
              value={issueData.title}
              onChange={(e) =>
                setIssueData({ ...issueData, title: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the issue"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Severity
              </label>
              <select
                value={issueData.severity}
                onChange={(e) =>
                  setIssueData({ ...issueData, severity: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={issueData.category}
                onChange={(e) =>
                  setIssueData({ ...issueData, category: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="vehicle">Vehicle Issue</option>
                <option value="driver">Driver Issue</option>
                <option value="route">Route Problem</option>
                <option value="cargo">Cargo Issue</option>
                <option value="documentation">Documentation</option>
                <option value="customer">Customer Issue</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Leg
            </label>
            <select
              value={issueData.legId}
              onChange={(e) =>
                setIssueData({ ...issueData, legId: parseInt(e.target.value) })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {job.legs.map((leg, index) => (
                <option key={leg.legId} value={leg.legId}>
                  {leg.legNumber}: {leg.origin.split(",")[0]} →{" "}
                  {leg.destination.split(",")[0]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={issueData.description}
              onChange={(e) =>
                setIssueData({ ...issueData, description: e.target.value })
              }
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Detailed description of the issue..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowIssueModal(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => handleIssueReport(issueData)}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Report Issue
            </Button>
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6 space-x-4">
      <Notification
        notification={notification}
        onClose={() => setNotification(null)}
      />
      <VehicleUpdateModal />
      <DriverUpdateModal />
      <PhotoRequestModal />
      <IssueReportModal />
      <StatusUpdateModal />
      <AddLegModal />
      <TollTrackerModal />

      {/* Enhanced Header */}
      <div className="flex items-center justify-between px-4">
        <div className="flex items-center">
          <Button onClick={() => window.history.back()} className="mr-4">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {job.jobNumber}
              </h1>
              <Badge variant={job.status === "Active" ? "success" : "default"}>
                {job.statusName}
              </Badge>
              <Badge
                variant={
                  job.priority === "High"
                    ? "danger"
                    : job.priority === "Medium"
                      ? "warning"
                      : "default"
                }
              >
                {job.priority} Priority
              </Badge>
            </div>
            <p className="text-gray-600 font-medium">
              {job.legs[0]?.origin} →{" "}
              {job.legs[job.legs.length - 1]?.destination}
            </p>
            <div className="flex items-center mt-1 text-sm text-gray-500 space-x-4">
              <span className="flex items-center">
                <Hash className="w-3 h-3 mr-1" />
                PO: {job.customerPO}
              </span>
              <span className="flex items-center">
                <Hash className="w-3 h-3 mr-1" />
                Tally: {job.tallySheetNo}
              </span>
              <span className="flex items-center">
                <Map className="w-3 h-3 mr-1" />
                {job.totalDistance}km • {job.legs.length} legs
              </span>
              <span className="flex items-center">
                <Timer className="w-3 h-3 mr-1" />
                {parseFloat(job.totalDuration.toFixed(2))}h estimated
              </span>
              <span className="flex items-center">
                <Package className="w-3 h-3 mr-1" />
                {job.type}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => get_job_details()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {/* <Button variant="outline" size="sm">
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button> */}
        </div>
      </div>

      {/* Overall Progress Bar */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Overall Job Progress
          </h3>
          <div className="flex items-center space-x-4">
            <Badge variant="blue" size="sm">
              {job.overallProgress}% Complete
            </Badge>
            <span className="text-sm text-gray-600">
              {job.legs.filter((leg) => leg.status === "Active").length} active
              legs
            </span>
            <span className="text-sm text-gray-600">
              Last Updated: {lastRefresh.toLocaleTimeString()}
            </span>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-gradient-to-r from-blue-500 to-green-500 h-4 rounded-full transition-all duration-1000 flex items-center justify-end pr-2"
            style={{ width: `${job.overallProgress}%` }}
          >
            <Truck className="w-3 h-3 text-white" />
          </div>
        </div>
        <div className="flex justify-between text-sm text-gray-600 mt-2">
          <span>Created: {new Date(job.createdAt).toLocaleDateString()}</span>
          <span>
            Type: {job.type} • Status: {job.statusName}
          </span>
          <span>
            ETA:{" "}
            {job.jobEndDate
              ? new Date(job.jobEndDate).toLocaleDateString()
              : "TBD"}
          </span>
        </div>
      </Card>

      {/* Tab Navigation */}
      <div>
        <Card className="flex space-x-8 p-0 px-8 mt-8">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-md transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center">
                  <TabIcon className="w-4 h-4 mr-2" />
                  {tab.label}
                  {tab.id === "legs" && (
                    <Badge variant="blue" size="sm" className="ml-2">
                      {job.legs.length}
                    </Badge>
                  )}
                </div>
              </button>
            );
          })}
        </Card>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "overview" && <OverviewTab />}
        {activeTab === "legs" && <LegsTab />}
        {activeTab === "timeline" && <TimelineTab />}
        {activeTab === "alerts" && <AlertsTab />}
        {activeTab === "documents" && <DocumentsTab />}
      </div>
    </div>
  );
};

export default JobDetailsPage;
